import { Injectable } from '@angular/core';

declare const google: any;

@Injectable({
  providedIn: 'root',
})
export class GooglePlacesService {
  // Built on first use, not at construction: when the Maps script fails to load (blocked,
  // offline, slow network) `google` is undefined, and touching it here threw inside the
  // injector and blanked every page that injects this service — registration included.
  // Without Maps, lookups resolve empty and the user types the address by hand.
  private get maps(): any {
    return typeof google !== 'undefined' && google?.maps?.places ? google.maps : null;
  }
  private _autocomplete: any;
  private get autocompleteService(): any {
    return this._autocomplete ??= this.maps && new this.maps.places.AutocompleteService();
  }
  private _places: any;
  private get placesService(): any {
    return this._places ??= this.maps && new this.maps.places.PlacesService(document.createElement('div'));
  }
  private _geocoder: any;
  private get geocoder(): any {
    return this._geocoder ??= this.maps && new this.maps.Geocoder();
  }

  getPlaces(query: string): Promise<any[]> {
    return new Promise((resolve) => {
      if (!query || query.length < 3 || !this.autocompleteService) {
        resolve([]);
        return;
      }
      this.autocompleteService.getPlacePredictions({ 
        input: query ,
        types :['address'] ,
        componentRestrictions: { country: 'US' }, 
      }, (predictions: any) => {
        if (predictions) {
          resolve(predictions);
        } else {
          resolve([]);
        }
      });
    });
  }
  
  /**
   * Florida addresses, ranked toward Putnam County. Bias (not strictBounds): a strict box
   * was set to Sarasota, so Putnam addresses got no suggestions, and a strict statewide box
   * ranks poorly. The Florida filter below keeps out-of-state results away.
   */
  getPlacesFlorida(query: string):  Promise<any[]> {
    if (!query || query.length < 3 || !this.autocompleteService) { return Promise.resolve([]); }
    const putnam = new google.maps.LatLngBounds(
      new google.maps.LatLng(29.40, -82.06), // Southwest corner of Putnam County
      new google.maps.LatLng(29.84, -81.41)  // Northeast corner
    );

    return new Promise((resolve) => {
      this.autocompleteService.getPlacePredictions({
        input: query,
        types: ['address'],
        bounds: putnam,
        componentRestrictions: { country: 'US' },
      }, (predictions: any) => {
        if (predictions) {
          const floridaPredictions = predictions.filter((prediction: { terms: any[]; }) => {
            return prediction.terms.some(term => term.value.toUpperCase() === 'FLORIDA' || term.value.toUpperCase() === 'FL');
          });
          resolve(floridaPredictions);
        } else {
          resolve([]);
        }
      });
    });
  }

  getPlaceDetails(placeId: string): Promise<any> {
    return new Promise((resolve) => {
      if (!this.placesService) { resolve(null); return; }
      this.placesService.getDetails({ placeId }, (place: any) => {
        resolve(place);
      });
    });
  }

  /**
   * Resolve city / state / state code from a US zip code.
   * Returns null when the zip can't be resolved (or Maps is unavailable).
   */
  getLocationByZip(zip: string): Promise<any | null> {
    return new Promise((resolve) => {
      if (!zip || zip.replace(/\D/g, '').length < 5 || !this.geocoder) {
        resolve(null);
        return;
      }
      this.geocoder.geocode(
        { address: zip, componentRestrictions: { country: 'US' } },
        (results: any[], status: string) => {
          if (status === 'OK' && results && results.length) {
            resolve(this.extractLocationDetails(results[0]));
          } else {
            resolve(null);
          }
        }
      );
    });
  }

  public extractLocationDetails(place: any) {
    const addressComponents = place.address_components;
    let city: string | null = null;
    let zipCode: string | null = null;
    let state: string | null = null;
    let stateCode: string | null = null;
   let street : string | null = null;
    for (const component of addressComponents) {
      if (component.types.includes('street_number')){
        street = component.long_name; // Full street name
      }
      if (component.types.includes('route')) {
        street = street ?  street + ' ' + component.long_name :  component.long_name; // Full street name
      }
      if (component.types.includes('locality')) {
        city = component.long_name; // or component.short_name
      }
      if (component.types.includes('postal_code')) {
        zipCode = component.long_name; // or component.short_name
      }
  
      if (component.types.includes('administrative_area_level_1')) {
        state = component.long_name; // Full state name
        stateCode = component.short_name; // State code (abbreviation)
      }
    }
  
    return {
      address: street,
      city: city,
      zip: zipCode,
      state: state,
      stateCode: stateCode,
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
    };
  }
}
